import {createContext, use, type ReactNode} from 'react'
import type {UserState} from '../lib/utils/rendering-strategy'
import {getCopilotPlanFlags, type UserCopilotPlanType, type UserOrgStatusType} from '../lib/utils/personalization'

/**
 * Context for user personalization data.
 * Currently contains user state for rendering strategies, but can be extended
 * to include org membership, subscription tier, or other user attributes.
 */
export type PersonalizationContextValue = {
  userState: UserState
}

export type PersonalizationUserStateProps = {
  userLoggedIn?: boolean
  userCopilotPlan?: UserCopilotPlanType
  orgMembershipType?: UserOrgStatusType
  orgAdminType?: UserOrgStatusType
}

export function buildPersonalizationValue({
  userLoggedIn,
  userCopilotPlan,
  orgMembershipType,
  orgAdminType,
}: PersonalizationUserStateProps): PersonalizationContextValue {
  const copilotPlanFlags = getCopilotPlanFlags(userCopilotPlan)

  return {
    userState: {
      isLoggedIn: userLoggedIn,
      ...copilotPlanFlags,
      isTeamMember: orgMembershipType === 'team',
      isEnterpriseMember: orgMembershipType === 'enterprise',
      isTeamAdmin: orgAdminType === 'team',
      isEnterpriseAdmin: orgAdminType === 'enterprise',
    },
  }
}

const PersonalizationContext = createContext<PersonalizationContextValue | undefined>(undefined)

export type PersonalizationProviderProps = {
  children: ReactNode
  value: PersonalizationContextValue
}

/**
 * Provider for personalization context. Mount at template level to make
 * user state available to all child components for conditional rendering.
 */
export function PersonalizationProvider({children, value}: PersonalizationProviderProps) {
  return <PersonalizationContext value={value}>{children}</PersonalizationContext>
}

/**
 * Hook to access personalization context.
 * Returns a default empty state if used outside of a PersonalizationProvider
 * to allow components to be used in isolation (e.g., Storybook, tests).
 */
export function usePersonalization(): PersonalizationContextValue {
  const context = use(PersonalizationContext)
  if (!context) {
    // Graceful fallback for components used outside personalization context
    return {userState: {}}
  }
  return context
}
